import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';

const ProgressContext = createContext();

const STORAGE_KEY = 'smc_30day_progress';
const initialState = {
  completedDays: [],
  quizAttempted: [],
  quizScores: {},
  userName: '',
  startDate: null
};

export const ProgressProvider = ({ children }) => {
  const [progress, setProgress] = useState(initialState);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isCloudLoaded, setIsCloudLoaded] = useState(false);
  const isInitialCloudLoad = useRef(true);
  const lastSavedJson = useRef('');

  const isKindeConfigured = Boolean(import.meta.env.VITE_KINDE_DOMAIN && import.meta.env.VITE_KINDE_CLIENT_ID);
  const kindeAuth = isKindeConfigured ? useKindeAuth() : null;
  const isAuthenticated = kindeAuth?.isAuthenticated;
  const getToken = kindeAuth?.getToken;
  const user = kindeAuth?.user;
  const userId = user?.id || user?.sub || null;

  // 1. Load progress from localStorage on initial mount
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setProgress(parsed);
        lastSavedJson.current = saved;
      } catch (error) {
        console.error('Failed to load local progress:', error);
      }
    }
  }, []);

  // 2. When auth state settles, load the correct progress for this user
  useEffect(() => {
    // Wait until we know the auth state
    if (!isKindeConfigured) {
      // No auth configured → just use local storage (existing behavior)
      setIsCloudLoaded(true);
      return;
    }
    if (kindeAuth?.isLoading) return;

    if (isAuthenticated && getToken) {
      // Logged in: fetch cloud progress, cloud wins
      async function fetchCloudProgress() {
        try {
          setIsSyncing(true);
          const token = await getToken();
          if (!token) return;

          const res = await fetch('/api/progress', {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (res.ok) {
            const result = await res.json();
            if (result.data && Object.keys(result.data).length > 0) {
              // Cloud progress wins (authoritative for this user's account)
              setProgress(result.data);
              localStorage.setItem(STORAGE_KEY, JSON.stringify(result.data));
              lastSavedJson.current = JSON.stringify(result.data);
            }
            // If cloud is empty, keep local (could be first login on this device)
          }
        } catch (err) {
          console.warn('Cloud progress fetch skipped/failed (will fallback to localStorage):', err);
        } finally {
          setIsSyncing(false);
          setIsCloudLoaded(true);
          isInitialCloudLoad.current = false;
        }
      }

      fetchCloudProgress();
    } else {
      // Logged out: clear to prevent leaking previous user's data
      setProgress(initialState);
      setIsCloudLoaded(true);
    }
  }, [isAuthenticated, getToken, isKindeConfigured, kindeAuth?.isLoading]);

  // 3. Save progress to localStorage whenever it changes
  useEffect(() => {
    const json = JSON.stringify(progress);
    if (json !== lastSavedJson.current) {
      localStorage.setItem(STORAGE_KEY, json);
      lastSavedJson.current = json;
    }
  }, [progress]);

  // 4. Debounced push to Cloudflare D1 whenever progress changes (if authenticated)
  useEffect(() => {
    if (!isAuthenticated || !getToken || isInitialCloudLoad.current || !isCloudLoaded) return;

    const timeoutId = setTimeout(async () => {
      try {
        setIsSyncing(true);
        const token = await getToken();
        if (!token) return;

        await fetch('/api/progress', {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ data: progress })
        });
      } catch (err) {
        console.error('Failed to sync progress to cloud:', err);
      } finally {
        setIsSyncing(false);
      }
    }, 1200);

    return () => clearTimeout(timeoutId);
  }, [progress, isAuthenticated, getToken, isCloudLoaded]);

  // Mark a chapter as completed
  const completeChapter = (day) => {
    setProgress(prev => ({
      ...prev,
      completedDays: [...new Set([...prev.completedDays, day])],
      startDate: prev.startDate || new Date().toISOString()
    }));
  };

  // Record quiz attempt and score
  const recordQuizScore = (day, score, totalQuestions = 10) => {
    setProgress(prev => ({
      ...prev,
      quizAttempted: [...new Set([...prev.quizAttempted, day])],
      quizScores: {
        ...prev.quizScores,
        [day]: score
      }
    }));
  };

  // Check if a day is unlocked
  const isDayUnlocked = (day) => {
    if (day === 1) return true;
    const prevDay = day - 1;
    return progress.completedDays.includes(prevDay) && 
           progress.quizAttempted.includes(prevDay);
  };

  // Check if chapter is completed
  const isChapterCompleted = (day) => {
    return progress.completedDays.includes(day);
  };

  // Check if quiz is attempted
  const isQuizAttempted = (day) => {
    return progress.quizAttempted.includes(day);
  };

  // Get quiz score for a day
  const getQuizScore = (day) => {
    return progress.quizScores[day] || 0;
  };

  // Calculate average score across all attempted quizzes
  const calculateAverageScore = () => {
    const scores = Object.values(progress.quizScores);
    if (scores.length === 0) return 0;
    const sum = scores.reduce((acc, score) => acc + score, 0);
    return (sum / scores.length).toFixed(2);
  };

  // Calculate overall completion percentage
  const calculateCompletionPercentage = () => {
    return ((progress.completedDays.length / 30) * 100).toFixed(0);
  };

  // Check certificate eligibility (all chapters + quizzes done, avg >= 60%)
  const isCertificateEligible = () => {
    const allDays = Array.from({ length: 30 }, (_, i) => i + 1);
    const allCompleted = allDays.every(day => progress.completedDays.includes(day));
    const allQuizzesAttempted = allDays.every(day => progress.quizAttempted.includes(day));
    const avgScore = parseFloat(calculateAverageScore());
    return allCompleted && allQuizzesAttempted && avgScore >= 60;
  };

  // Set user name
  const setUserName = (name) => {
    setProgress(prev => ({
      ...prev,
      userName: name
    }));
  };

  // Reset all progress
  const resetProgress = () => {
    setProgress(initialState);
    localStorage.removeItem(STORAGE_KEY);
  };

  const value = {
    progress,
    isSyncing,
    isCloudLoaded,
    userId,
    isAuthenticated,
    completeChapter,
    recordQuizScore,
    isDayUnlocked,
    isChapterCompleted,
    isQuizAttempted,
    getQuizScore,
    calculateAverageScore,
    calculateCompletionPercentage,
    isCertificateEligible,
    setUserName,
    resetProgress
  };

  return (
    <ProgressContext.Provider value={value}>
      {children}
    </ProgressContext.Provider>
  );
};

export const useProgress = () => {
  const context = useContext(ProgressContext);
  if (!context) {
    throw new Error('useProgress must be used within a ProgressProvider');
  }
  return context;
};
